import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";

import { frontendConfig } from "../common/api/config.js";
import type { MeDTO, UserPresence } from "../common/api/types.js";
import { Avatar, PRESENCE_LABELS } from "../common/components/Avatar.js";
import { useRemoveAvatar, useSetAvatar, useSetPresence, useUpdateSettings } from "./profileQueries.js";

const PRESENCE_ORDER: UserPresence[] = ["AVAILABLE", "AWAY", "BUSY", "OFFLINE"];

const DOT_COLORS: Record<UserPresence, string> = {
  AVAILABLE: "bg-green-500",
  AWAY: "bg-amber-400",
  BUSY: "bg-red-500",
  OFFLINE: "bg-slate-400",
};

/** Avatar oben rechts: zeigt die eigene Erreichbarkeit und öffnet das Konto-Menü. */
export function AccountMenu({
  me,
  onSignOut,
  onSelectTenant,
  switchingTenant = false,
}: {
  me: MeDTO;
  onSignOut: () => void;
  /** Mandant wechseln. Auf dem Telefon steht die Wahl hier statt in der Kopfzeile. */
  onSelectTenant?: (slug: string) => void;
  switchingTenant?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const setPresence = useSetPresence();
  const setAvatar = useSetAvatar();
  const removeAvatar = useRemoveAvatar();
  const updateSettings = useUpdateSettings();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const busy = setPresence.isPending || setAvatar.isPending || removeAvatar.isPending;
  const error = setAvatar.error ?? setPresence.error ?? removeAvatar.error;

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${me.user.displayName} · ${PRESENCE_LABELS[me.user.presence]}`}
        className="flex items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <span className="sr-only">Konto und Erreichbarkeit</span>
        <Avatar
          displayName={me.user.displayName}
          avatarUrl={me.user.avatarUrl}
          presence={me.user.presence}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-line bg-surface-raised p-3 shadow-xl"
        >
          <div className="mb-3 flex items-center gap-3">
            <Avatar
              displayName={me.user.displayName}
              avatarUrl={me.user.avatarUrl}
              presence={me.user.presence}
              size="lg"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">{me.user.displayName}</p>
              <p className="truncate text-xs text-ink-muted">{me.user.email}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => fileInput.current?.click()}
                className="mt-1 text-xs text-brand hover:underline disabled:opacity-50"
              >
                {setAvatar.isPending ? "Bild wird gesetzt …" : "Bild ändern"}
              </button>
              {me.user.avatarUrl ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => removeAvatar.mutate()}
                  className="ml-2 mt-1 text-xs text-ink-muted hover:underline disabled:opacity-50"
                >
                  entfernen
                </button>
              ) : null}
            </div>
          </div>

          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(changed) => {
              const file = changed.target.files?.[0];
              changed.target.value = "";
              if (file) {
                setAvatar.mutate(file);
              }
            }}
          />

          {/* Der Mandant steht auf dem Telefon hier: in der Kopfzeile nimmt sein Name
              den Platz weg, den dort der Titel der App braucht. Auf breiten Fenstern
              bleibt er oben, deshalb hier nur bis sm. */}
          {onSelectTenant && me.memberships.length > 0 ? (
            <div className="mb-2 sm:hidden">
              <p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Unternehmen
              </p>
              {me.memberships.length === 1 ? (
                <p className="px-2 py-1 text-sm text-ink">{me.activeTenant?.name ?? ""}</p>
              ) : (
                <ul>
                  {me.memberships.map((membership) => (
                    <li key={membership.id}>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={membership.tenant.slug === me.activeTenant?.slug}
                        disabled={switchingTenant}
                        onClick={() => onSelectTenant(membership.tenant.slug)}
                        className={[
                          "w-full rounded-lg px-2 py-2 text-left text-sm",
                          membership.tenant.slug === me.activeTenant?.slug
                            ? "bg-brand/10 text-brand"
                            : "text-ink hover:bg-surface-sunken",
                        ].join(" ")}
                      >
                        {membership.viaInternal
                          ? `${membership.tenant.name} (intern)`
                          : membership.tenant.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            Erreichbarkeit
          </p>
          <ul className="mb-2">
            {PRESENCE_ORDER.map((presence) => (
              <li key={presence}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={me.user.presence === presence}
                  disabled={busy}
                  onClick={() => setPresence.mutate(presence)}
                  className={[
                    "flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm",
                    me.user.presence === presence
                      ? "bg-brand/10 text-brand"
                      : "text-ink hover:bg-surface-sunken",
                  ].join(" ")}
                >
                  <span
                    aria-hidden="true"
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${DOT_COLORS[presence]}`}
                  />
                  {PRESENCE_LABELS[presence]}
                </button>
              </li>
            ))}
          </ul>

          <p className="mb-1 mt-3 px-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">Darstellung</p>
          <div className="mb-2 grid grid-cols-3 gap-1 rounded-lg bg-surface-sunken p-1">
            {([{ id: "system", label: "System", Icon: Monitor }, { id: "light", label: "Hell", Icon: Sun }, { id: "dark", label: "Dunkel", Icon: Moon }] as const).map(({ id, label, Icon }) => <button key={id} type="button" disabled={updateSettings.isPending} onClick={() => updateSettings.mutate({ theme: id })} className={(me.settings.theme ?? "system") === id ? "flex flex-col items-center gap-1 rounded-md bg-surface-raised px-1 py-2 text-[10px] font-bold text-brand shadow-sm" : "flex flex-col items-center gap-1 rounded-md px-1 py-2 text-[10px] font-semibold text-ink-muted hover:text-ink"}><Icon className="h-4 w-4" />{label}</button>)}
          </div>

          {error ? <p className="mb-2 px-1 text-xs text-danger">{error.message}</p> : null}

          <button
            type="button"
            onClick={onSignOut}
            className="w-full rounded-lg px-2 py-2 text-left text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            Abmelden
          </button>

          <div className="mt-2 border-t border-line px-2 pt-2 text-xs text-ink-muted">
            <p className="font-medium text-ink">buma</p>
            <p>Community Project · Version {frontendConfig().version}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
